import React, { useEffect, useRef, useState } from "react";
import {
  LayoutRectangle,
  Platform,
  StyleSheet,
  View,
  TouchableOpacity,
} from "react-native";
import { useCloseFromOutside } from "../../hooks/useCloseFromOutside";
import { useResponsive } from "../../hooks/useResponsive";
import { parseSecondsInMinutes } from "../../utils/TimeCalculator";
import Icon from "../Icon";
import RText from "../RText";
import LiveChannel from "./LiveChannel";
import ProgressBar from "./ProgressBar";
import VolumeControl from "./VolumeControl";
import variables from "../../styles/variables";
import { useAtom, useSetAtom } from "jotai";
import {
  liveStreamFullScreenAtom,
  loadableLiveChannelsAtom,
} from "../../atoms";

export type ControlBarProps = {
  timer: {
    currentTime: number;
    duration: number;
  };
  isPlaying: boolean;
  isFullScreen: boolean;
  isLive?: boolean;
  isAdPlaying?: boolean;
  isFocused?: boolean;
  castButtonProps: {
    currentSource: string;
    title: string;
    subtitle: string;
    poster: string;
    contentType: string;
  };
  onVolumeChange: (volume: number) => void;
  onMutePress: (isMuted: boolean) => void;
  onPlayPausePress?: () => void;
  onFullScreenPress?: () => void;
  onSeek?: (seconds: number) => void;
  onPopOutPress?: () => void;
  onPopupOnpen?: () => void;
  onHover?: () => void;
  dynamicGridIndex?: number;
  setLiveChannelButtonSize?: (layout: LayoutRectangle) => void;
  displayLiveChannel?: boolean;
  setDisplayLiveChannel?: (value: boolean) => void;
};

const isWeb = Platform.OS === "web";

const ControlBar: React.FC<ControlBarProps> = ({
  timer,
  isFocused,
  isPlaying,
  isFullScreen,
  isLive,
  isAdPlaying,
  onVolumeChange,
  onMutePress,
  onPlayPausePress,
  onFullScreenPress,
  onSeek,
  onPopOutPress,
  onPopupOnpen,
  onHover,
  dynamicGridIndex,
  setLiveChannelButtonSize,
  displayLiveChannel,
  setDisplayLiveChannel,
}) => {
  const { isMediumAndDown, isLargeAndUp } = useResponsive();
  const [liveChannels] = useAtom(loadableLiveChannelsAtom);
  const [displayVolumeControl, setDisplayVolumeControl] = useState(false);
  const [volumeHovered, setVolumeHovered] = useState(false);

  const closeMenusRef = useCloseFromOutside(
    displayVolumeControl,
    setDisplayVolumeControl
  );
  const closeLiveChannelRef = useCloseFromOutside(
    displayLiveChannel,
    setDisplayLiveChannel
  );
  const { currentTime, duration } = timer;
  const currentTimeFormatted = parseSecondsInMinutes(currentTime || 0);
  const durationFormatted = parseSecondsInMinutes(duration || 1);
  const [isMuted, setIsMuted] = useState(false);
  const refContainer = useRef(null);
  const setLiveStreamFullScreen = useSetAtom(liveStreamFullScreenAtom);

  const hoverEventListenerHandler = (e) => {
    onHover?.();
  };

  useEffect(() => {
    if (isLive) {
      if (isFocused) setIsMuted(false);
      else setIsMuted(true);
    }
  }, [isFocused]);

  useEffect(() => {
    if (displayLiveChannel || displayVolumeControl) {
      onPopupOnpen?.();
    }
  }, [displayLiveChannel, displayVolumeControl]);

  useEffect(() => {
    if (volumeHovered) setDisplayVolumeControl(true);
    else setDisplayVolumeControl(false);
  }, [volumeHovered]);

  useEffect(() => {
    onMutePress(isMuted);
  }, [isMuted]);

  useEffect(() => {
    if (!isWeb) return;
    refContainer.current?.addEventListener(
      "mousemove",
      hoverEventListenerHandler
    );
    return () => {
      refContainer.current?.removeEventListener(
        "mousemove",
        hoverEventListenerHandler
      );
    };
  }, [refContainer]);

  const handleLiveChannel = () => {
    setDisplayLiveChannel((prev) => !prev);
  };

  return (
    <View ref={refContainer} style={[styles.container]}>
      <TouchableOpacity
        accessibilityLabel={`play-pause-button-${dynamicGridIndex}`}
        style={styles.playPause}
        onPress={() => onPlayPausePress?.()}
      >
        <Icon
          name={isPlaying ? "pauseVideo" : "playVideo"}
          fill="palette.rtv.white"
          size={isFullScreen ? "medium" : "small"}
        />
      </TouchableOpacity>
      {!isLive && !isAdPlaying && durationFormatted !== "00:01" && (
        <View style={[styles.timer, isLargeAndUp && { flex: 2, minWidth: 90 }]}>
          <RText
            size={isMediumAndDown && !isFullScreen ? "xs" : "sm"}
            color="white"
          >{`${currentTimeFormatted} / ${durationFormatted}`}</RText>
        </View>
      )}
      <View style={[styles.progressBar, isLargeAndUp && { flex: 12 }]}>
        {!isLive && (
          <ProgressBar
            isAdPlaying={isAdPlaying}
            timer={timer}
            onSeek={(seconds) => onSeek?.(seconds)}
            isLive={isLive}
          />
        )}
      </View>
      <View ref={closeMenusRef} style={styles.volume}>
        <TouchableOpacity
          accessibilityLabel={`volume-button-${dynamicGridIndex}`}
          // @ts-ignore
          onMouseEnter={() => setVolumeHovered(true)}
          onMouseLeave={() => setVolumeHovered(false)}
          onPress={() => setIsMuted((prev) => !prev)}
          style={styles.pressable}
        >
          <Icon
            name={isMuted ? "mutedVolume" : "volume"}
            fill="palette.rtv.white"
            size={isFullScreen ? "medium" : "small"}
          />
          <VolumeControl
            onVolumeChange={(volume) => {
              onVolumeChange(volume);
              setIsMuted(volume === 0);
            }}
            isVisible={displayVolumeControl}
            isMuted={isMuted}
          />
        </TouchableOpacity>
      </View>
      {isWeb && (
        <TouchableOpacity
          style={styles.popout}
          onPress={() => onPopOutPress?.()}
        >
          <Icon
            name="popout"
            fill="palette.rtv.white"
            size={isFullScreen ? "medium" : "small"}
          />
        </TouchableOpacity>
      )}
      {isLive && liveChannels.state === "hasData" && (
        <View
          accessible
          accessibilityLabel={`live-channel-button-${dynamicGridIndex}`}
          onLayout={(e) => setLiveChannelButtonSize(e.nativeEvent.layout)}
          ref={closeLiveChannelRef}
          style={styles.liveChannel}
        >
          <TouchableOpacity
            accessible
            onPress={handleLiveChannel}
            style={styles.pressable}
          >
            <Icon
              name="rtvLogo"
              fill="palette.rtv.white"
              size={isFullScreen ? "medium" : "small"}
            />
          </TouchableOpacity>
        </View>
      )}
      <TouchableOpacity
        accessibilityLabel={`fullscreen-button-${dynamicGridIndex}`}
        style={styles.fullScreen}
        onPress={() => {
          isLive && isWeb
            ? setLiveStreamFullScreen((prev) => !prev)
            : onFullScreenPress?.();
        }}
      >
        <Icon
          name={isFullScreen ? "exitFullScreen" : "enterFullScreen"}
          fill="palette.rtv.white"
          size={isFullScreen ? "medium" : "small"}
        />
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    // backgroundColor: variables.colors.palette.rtv.primary,
    minHeight: isWeb ? variables.spacing.large : variables.spacing.medium,
    width: "100%",
    paddingLeft: variables.spacing.xxsmall,
    paddingRight: variables.spacing.xxsmall,
  },
  playPause: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    minWidth: variables.spacing.medium,
  },
  timer: {
    flex: 3,
    justifyContent: "center",
    alignItems: "center",
    minWidth: variables.spacing.large,
  },
  progressBar: {
    flex: 5,
    marginHorizontal: variables.spacing.xsmall,
    minWidth: variables.spacing.xsmall,
  },
  volume: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    minWidth: variables.spacing.medium,
  },
  popout: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    minWidth: variables.spacing.medium,
  },
  liveChannel: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    minWidth: variables.spacing.medium,
  },
  fullScreen: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    minWidth: variables.spacing.medium,
  },
  pressable: {
    height: "100%",
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
});

export default ControlBar;
